import React, { useCallback, useMemo } from 'react';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

import { useEditorMode } from '../EditorModeContext';
import { EditorNodeKind } from '../editorCategories';
import { useMapEditor } from '../MapEditorContext';
import { useMapEditorCatalog } from '../useMapEditorCatalog';
import {
  chainEndsInCap,
  findCatalogElement,
  isMissingFromKit,
  type MapEditorCatalogElement,
} from '../mapEditorCatalog';
import { roomIdsOf } from '../mapEditorRooms';
import { describeSpotCount } from '../mapEditorSpotText';
import { resolveStarters } from '../mapEditorStarters';
import { findRefusalFor, START_ROOM_KEY } from '../mapEditorUnreal';
import { useSceneTreeStyles } from '../SceneTree/styles';
import { ChainPlanGlyph } from './PlanGlyph';
import { useLayoutTreeStyles } from './treeStyles';
import { useSegmentPicker } from './SegmentPickerHost';

/**
 * The walk, in the Scene tree: the entrance, the segments in order, and the wall that closes it.
 *
 * This is where the redesign put the layout, and the reason is what the tree already was. It lists
 * every spot, every hotspot and every script in the scene one by one; `Layout` was the only group
 * with a single stand-in row, because the segment list lived in the right-hand panel. That left the
 * Scene panel with one row and roughly 500px of nothing under it, and the right-hand panel doing two
 * jobs — the whole layout *and* whichever part of it was being worked on.
 *
 * Selecting a row opens the segment panel for it. The controls that act on a segment — reorder,
 * remove, change — are in that panel rather than on the row: the tree is 340px and the inspector is
 * 360px, and three icons plus a name does not fit either at a size anybody can hit.
 */
export const LayoutTree = () => {
  const { t: translate } = useTranslation();
  const treeStyles = useSceneTreeStyles();
  const styles = useLayoutTreeStyles();
  const { rooms, startElement, setChain, refusals, previewRefusals, isDirty } =
    useMapEditor();
  const { selectedNode, selectMapSegment } = useEditorMode();
  const { elements: catalogElements, defaultEndElement } =
    useMapEditorCatalog();
  const { openForAppend, openForSegment } = useSegmentPicker();

  const selectedKey =
    selectedNode?.kind === EditorNodeKind.MAP_SEGMENT
      ? selectedNode.roomKey
      : '';

  // Whether the selection still names something the picker can act on. A removal leaves the selection
  // pointing at the key it removed — the panel says so and offers nothing, which is the right answer
  // there — so without this the tree would go on offering **Change the selected segment** for a row
  // that is gone, and the commit would find no index, return, and close the dialog having done
  // nothing. Silently: the author picks a module, presses the button and the layout is unchanged.
  const isSelectionLive =
    selectedKey === START_ROOM_KEY ||
    rooms.some((room) => room.key === selectedKey);

  // Same rule the panel applies: a dirty draft is described by the draft's own report, because the
  // published one was keyed against a chain the author has since edited.
  const activeRefusals = isDirty ? previewRefusals : refusals;

  // The wall that closes the walk. A row rather than a segment, because it is not the author's to
  // move, remove or reorder — it is appended at publish time and always goes last. Hiding it would
  // leave them wondering where the extra room came from.
  //
  // Not shown for a chain that already ends in one, on the same rule the payload builder applies: a
  // restored layout can carry a cap as a picked segment, and nothing is appended behind it.
  //
  // Shown for an empty chain too, which is the case it was missing from. A fresh configuration opens
  // on the entrance alone and that is correct — but the entrance stands on the level with its exit
  // hanging open, so the persistence bridge sends the cap on its own the moment the author enters
  // (see the empty-layout republish in EditorMode.tsx, and appendEndCap for why a lone cap is a real
  // layout). The wall is therefore already standing in the world while this tree said there was
  // none, and the author was left reading their own configuration as unfinished until their first
  // edit made the row appear.
  const endElement =
    defaultEndElement && !chainEndsInCap(roomIdsOf(rooms), catalogElements)
      ? findCatalogElement(catalogElements, defaultEndElement)
      : undefined;

  const entranceElement = startElement
    ? findCatalogElement(catalogElements, startElement)
    : undefined;

  // Memoised because the tree re-renders on every selection change, and resolving a starter walks the
  // whole kit three times.
  const starters = useMemo(
    () => resolveStarters(catalogElements),
    [catalogElements],
  );

  const handlePickStarter = useCallback(
    (elements: MapEditorCatalogElement[]) => {
      setChain(elements.map((element) => element.id));
      // Deliberately not selected. A starter drops in a whole walk, and selecting one of its segments
      // would put the author inside a single room at the moment the interesting thing on screen is
      // the shape of the whole thing.
    },
    [setChain],
  );

  return (
    <Box sx={styles.root}>
      {/* The entrance is a placement like any other now: selectable, swappable for any room in the
          kit, and it carries its own frames and its own surfaces. What it is not is a link in the
          chain — it is never moved or removed — so it sits above the numbered rows rather than in
          them. */}
      <Box
        sx={[
          treeStyles.node,
          selectedKey === START_ROOM_KEY && treeStyles.nodeSelected,
        ]}
        onClick={() => selectMapSegment(START_ROOM_KEY)}
        data-testid="map-editor-tree-entrance"
      >
        <HomeOutlinedIcon sx={[treeStyles.nodeIcon, styles.entranceIcon]} />
        <Typography sx={treeStyles.nodeLabel}>
          {entranceElement
            ? entranceElement.displayName
            : translate(
                'editorMode.mapEditorEntranceMapDefault',
                "The map's own entrance",
              )}
        </Typography>
        {/* An entrance named by the record that this build's kit cannot describe — chosen on a
            different build, and the one case where the label's "the map's own entrance" fallback is
            not the truth. The label is left as it is: it says what the world will actually open on,
            since an id UE cannot resolve is never sent and the map's baked entrance stands. The
            marker says why the author's choice is not the one on screen. */}
        {!!startElement && !entranceElement && (
          <Tooltip
            title={translate(
              'editorMode.mapEditorRowMissing',
              'Not in this build — open it to see why.',
            )}
          >
            <ReportProblemOutlinedIcon
              sx={styles.rowMissing}
              data-testid="map-editor-tree-missing-entrance"
            />
          </Tooltip>
        )}
        {selectedKey !== START_ROOM_KEY && (
          <Typography sx={treeStyles.groupCount}>
            {entranceElement
              ? describeSpotCount(entranceElement, translate)
              : translate('editorMode.mapEditorEntranceBaked', 'as built')}
          </Typography>
        )}
      </Box>

      {rooms.map((room, index) => {
        const element = findCatalogElement(catalogElements, room.id);
        const refusal = findRefusalFor(activeRefusals, room.key, index);
        const isSelected = selectedKey === room.key;
        // A room this build's kit has no entry for (MV-2004). Every other marker on this row
        // describes something the world did with the segment; this one describes the segment not
        // being one of this build's rooms at all, which is why the label above it is a raw id.
        const isMissing = isMissingFromKit(catalogElements, room.id);

        return (
          <Box
            key={room.key || `${room.id}-${index}`}
            sx={[treeStyles.node, isSelected && treeStyles.nodeSelected]}
            onClick={() => selectMapSegment(room.key)}
            data-testid={`map-editor-tree-segment-${room.key}`}
          >
            <Box sx={styles.grip} />
            <Typography sx={treeStyles.nodeLabel}>
              {element?.displayName ?? room.id}
            </Typography>
            {/* Missing outranks refused, and the two go together: an id the kit cannot describe is
                published without its family, direction or size, so UE turns it down and the row
                collects a refusal as well. Drawing both would say the same thing twice, and the
                refusal is the half that names the wrong cause — it reads as a layout the author can
                rearrange, when the only way out is to swap the room. */}
            {isMissing && (
              <Tooltip
                title={translate(
                  'editorMode.mapEditorRowMissing',
                  'Not in this build — open it to see why.',
                )}
              >
                <ReportProblemOutlinedIcon
                  sx={styles.rowMissing}
                  data-testid={`map-editor-tree-missing-${room.key}`}
                />
              </Tooltip>
            )}
            {/* The one marker that stays on the row. A refused segment is the answer to "why is this
                not in the world", and an author scanning the walk for the gap has to be able to find
                it without opening every row. */}
            {!isMissing && refusal && (
              <Tooltip
                title={translate(
                  'editorMode.mapEditorRowRefused',
                  'Not standing — open it to see why.',
                )}
              >
                <BlockOutlinedIcon
                  sx={styles.rowRefused}
                  data-testid={`map-editor-tree-refused-${room.key}`}
                />
              </Tooltip>
            )}
            {!isSelected && !refusal && !isMissing && (
              <Typography sx={treeStyles.groupCount}>
                {describeSpotCount(element, translate)}
              </Typography>
            )}
          </Box>
        );
      })}

      {!!endElement && (
        <Box
          sx={[treeStyles.node, styles.fixedNode]}
          data-testid="map-editor-tree-end-wall"
        >
          <LockOutlinedIcon sx={[treeStyles.nodeIcon, styles.fixedIcon]} />
          <Typography sx={treeStyles.nodeLabel}>
            {translate('editorMode.mapEditorEndWall', 'End wall')}
          </Typography>
        </Box>
      )}

      <Button
        variant="outlined"
        size="small"
        startIcon={<AddOutlinedIcon />}
        sx={treeStyles.addButton}
        onClick={openForAppend}
        data-testid="map-editor-tree-add"
      >
        {translate('editorMode.mapEditorAddSegment', 'Add a segment')}
      </Button>

      {/* Only while the walk is empty. An empty museum is the one moment an author has nothing to
          react to — every other state of this editor is a change to something already on screen —
          and one click puts a whole walk in, editable from the next moment on. */}
      {!rooms.length && !!starters.length && (
        <Box sx={styles.starters}>
          <Box sx={styles.startersLabel}>
            <Box sx={styles.startersRule} />
            <Typography sx={styles.startersLabelText}>
              {translate('editorMode.mapEditorStartFrom', 'or start from')}
            </Typography>
            <Box sx={styles.startersRule} />
          </Box>

          {starters.map((starter) => (
            <Box
              key={starter.id}
              sx={styles.starter}
              onClick={() => handlePickStarter(starter.elements)}
              data-testid={`map-editor-starter-${starter.id}`}
            >
              <Box sx={styles.starterGlyph}>
                <ChainPlanGlyph
                  elements={starter.elements}
                  width={36}
                  height={28}
                />
              </Box>
              <Box sx={styles.starterText}>
                <Typography sx={styles.starterName}>
                  {translate(starter.nameKey, starter.nameFallback)}
                </Typography>
                <Typography sx={styles.starterHint}>
                  {translate(starter.hintKey, starter.hintFallback)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Reachable from every row, and the reason it is here as well as in the panel: an author who
          has just changed one segment usually wants to change its neighbour, and the panel's Change
          only ever acts on the row already open. */}
      {!!selectedKey && isSelectionLive && (
        <Button
          variant="text"
          size="small"
          sx={styles.changeSelected}
          onClick={() => openForSegment(selectedKey)}
          data-testid="map-editor-tree-change-selected"
        >
          <MeetingRoomOutlinedIcon sx={styles.changeSelectedIcon} />
          {translate(
            'editorMode.mapEditorChangeSelected',
            'Change the selected segment',
          )}
        </Button>
      )}
    </Box>
  );
};

export default LayoutTree;
